import {remoteForm} from '@github-ui/remote-form'
import {on} from 'delegated-events'

// Billing writes submitted from the enterprise activation page. Submitting them in the background keeps their
// server time out of the activation page's navigation timing; the page is loaded again once the write completes.
const ACTIVATION_FORMS = [
  'form[action$="/settings/billing/update_payment_information"]',
  'form[action$="/settings/billing/update_payment_method"]',
  'form[action*="/billing/shipping_information"]',
]
  .map(form => `[data-activation-interstitial] ${form}`)
  .join(', ')

// Locked forms, mapped to how their page recovers if it is cached or restored before navigating away.
const pendingForms = new WeakMap<HTMLFormElement, () => void>()

on(
  'submit',
  ACTIVATION_FORMS,
  event => {
    if (pendingForms.has(event.currentTarget as HTMLFormElement)) event.preventDefault()
  },
  {capture: true},
)

function showPendingRecovery() {
  for (const form of document.querySelectorAll<HTMLFormElement>(ACTIVATION_FORMS)) {
    pendingForms.get(form)?.()
  }
}

document.addEventListener('turbo:before-cache', showPendingRecovery)

window.addEventListener('pageshow', event => {
  if (event.persisted) showPendingRecovery()
})

remoteForm(ACTIVATION_FORMS, async (form, wants, request) => {
  const container = form.closest<HTMLElement>('[data-activation-interstitial]')
  const content = container?.querySelector<HTMLElement>('[data-activation-interstitial-content]')
  const waiting = container?.querySelector<HTMLElement>('[data-activation-interstitial-waiting]')
  const recovery = container?.querySelector<HTMLElement>('[data-activation-interstitial-error]')
  if (!container || !content || !waiting || !recovery) return

  let showingRecovery = false
  const showRecovery = () => {
    showingRecovery = true
    waiting.hidden = true
    recovery.hidden = false
    // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-after-write -- Reveal the recovery state before focusing it.
    recovery.focus()
  }
  pendingForms.set(form, showRecovery)
  request.body?.append('activation_interstitial', '1')
  content.hidden = true
  waiting.hidden = false
  // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-after-write -- Move focus off the hidden form.
  waiting.focus()

  try {
    const response = await wants.json()
    if (!container.isConnected || showingRecovery) return

    const redirectUrl: unknown = response.json.redirect_url
    if (typeof redirectUrl !== 'string' || !redirectUrl) throw new Error('Missing activation redirect')
    const destination = new URL(redirectUrl, window.location.href)
    if (destination.origin !== window.location.origin) throw new Error('Invalid activation redirect')
    pendingForms.set(form, () => window.location.reload())
    window.location.assign(destination.href)
  } catch {
    if (!container.isConnected || showingRecovery) return

    // A failed response does not prove the write failed, so never resubmit automatically.
    showRecovery()
  }
})
